<template>
  <div>
    <div style="text-align: center">
      <h4>{{ isEdit ? 'Edit Integrasi Mayar.id' : 'Tambah Integrasi Mayar.id' }}</h4>
    </div>
    <br>
    <form @submit.prevent="submit">
      <label>Nama Toko / Integrasi <span class="text-danger">*</span></label>
      <input type="text" class="form-control" v-model="form.name" required
        placeholder="contoh: Toko Ebook, Kursus Premium, dll">
      <br>

      <label>Device Pengirim <span class="text-danger">*</span></label>
      <select v-model="form.device_id" class="form-control" required>
        <option :value="null" disabled>== Pilih Device ==</option>
        <option v-for="d in devices" :key="d.id" :value="d.id">
          {{ d.name }} ({{ d.status }})
        </option>
      </select>
      <br>

      <label>Webhook Signature (dari Mayar.id) <small class="text-muted">— opsional</small></label>
      <input type="text" class="form-control" v-model="form.webhook_signature"
        :placeholder="isEdit && !signatureChanged ? '(tersimpan, ketik baru untuk ganti)' : 'Paste secret token dari dashboard Mayar (header x-callback-token)'"
        @input="signatureChanged = true">
      <small class="text-muted">
        Diset di dashboard Mayar saat register webhook. Digunakan untuk verifikasi header <code>x-callback-token</code>.
        <b>Kalau dikosongkan, signature tidak divalidasi</b> (kurang aman, tapi webhook tetap bekerja).
      </small>
      <br><br>

      <label>Filter Event <small class="text-muted">— opsional, kosong = semua event</small></label>
      <input type="text" class="form-control" v-model="form.event_filter"
        placeholder="payment.received,payment.reminder">
      <small class="text-muted">
        Pisahkan dengan koma. Event yang tidak di list akan di-skip. Contoh event Mayar:
        <code>payment.received</code>, <code>payment.reminder</code>.
      </small>
      <br><br>

      <label>Nomor Admin (untuk notifikasi sales masuk)</label>
      <input type="text" class="form-control" v-model="form.admin_phones"
        placeholder="628xxxxxxxxx, bisa multi pisah koma">
      <small class="text-muted">Kosongkan kalau tidak perlu notif ke admin. Pisahkan dengan koma untuk multi admin.</small>
      <br><br>

      <div class="row">
        <div class="col-md-6">
          <label>Template Pesan ke Customer</label>
          <textarea v-model="form.template_customer" class="form-control" rows="10"
            placeholder="Halo {name}, terima kasih sudah membeli..."></textarea>
        </div>
        <div class="col-md-6">
          <label>Template Pesan ke Admin</label>
          <textarea v-model="form.template_admin" class="form-control" rows="10"
            placeholder="Sales baru: {name} ({phone}) beli {product_name}..."></textarea>
        </div>
      </div>
      <br>

      <div class="alert alert-secondary">
        <b>Placeholder yang tersedia:</b><br>
        <code>{event}</code> — tipe event (payment.received, dll)<br>
        <code>{name}</code>, <code>{email}</code>, <code>{phone}</code> — info customer<br>
        <code>{amount}</code> — total tagihan format Rupiah<br>
        <code>{product_name}</code>, <code>{product_type}</code> — info produk<br>
        <code>{transaction_id}</code> — ID transaksi Mayar<br>
        <code>{coupon_code}</code>, <code>{status}</code>, <code>{created_at}</code>, <code>{merchant_id}</code>
      </div>

      <label>
        <input type="checkbox" v-model="form.is_active_bool">
        &nbsp;Aktif
      </label>
      <br><br>

      <!-- Webhook URL (hanya di mode edit) -->
      <div v-if="isEdit && webhookUrl" class="card" style="background:#f8f9fa">
        <div class="card-body p-3">
          <label class="mb-1"><b>Webhook URL</b> (copy ini ke Mayar.id dashboard):</label>
          <div class="d-flex align-items-center">
            <input type="text" readonly :value="webhookUrl" class="form-control"
              style="font-family: monospace; font-size: 12px;" @focus="$event.target.select()">
            <button type="button" class="btn btn-light ml-2" @click="copyUrl">
              <i class="fas fa-copy"></i> Copy
            </button>
          </div>
          <small class="text-muted mt-1 d-block">
            Paste URL ini di Mayar dashboard → <b>API & Integration → Webhook</b>. Saat register, set <b>Webhook Signature</b> (secret) lalu paste juga di field signature di atas untuk verifikasi.
          </small>
        </div>
      </div>
      <br>

      <div class="text-right">
        <button type="button" class="btn btn-secondary" @click="$modal.hide('mayar_form')">Batal</button>
        <button type="submit" class="btn btn-primary ml-2" :disabled="isSaving">
          <span v-if="isSaving" class="spinner-border spinner-border-sm mr-2"></span>
          {{ isEdit ? 'Simpan Perubahan' : 'Tambah' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
const DEFAULT_CUSTOMER_TEMPLATE = `Halo {name}! 🎉

Terima kasih sudah membeli *{product_name}* di toko kami.

Detail pesanan:
• Produk: {product_name}
• Total: Rp {amount}
• Status: {status}
• Ref: {transaction_id}

Link akses akan kami kirim ke email ({email}).

Terima kasih 🙏`;

const DEFAULT_ADMIN_TEMPLATE = `🔔 *SALES BARU MAYAR*

Event: {event}
Customer: {name} ({phone})
Email: {email}
Produk: {product_name}
Total: Rp {amount}
Ref: {transaction_id}
Voucher: {coupon_code}`;

export default {
  props: {
    editItem: { type: Object, default: null },
    devices: { type: Array, default: () => [] },
  },
  data() {
    return {
      form: {
        name: "",
        device_id: null,
        webhook_signature: "",
        event_filter: "",
        admin_phones: "",
        template_customer: DEFAULT_CUSTOMER_TEMPLATE,
        template_admin: DEFAULT_ADMIN_TEMPLATE,
        is_active_bool: true,
      },
      isSaving: false,
      signatureChanged: false,
      webhookUrl: "",
    };
  },
  computed: {
    isEdit() {
      return !!(this.editItem && this.editItem.id);
    },
  },
  watch: {
    editItem: {
      immediate: true,
      handler(val) {
        if (val && val.id) {
          this.form = {
            name: val.name || "",
            device_id: val.device_id || null,
            webhook_signature: "",
            event_filter: val.event_filter || "",
            admin_phones: val.admin_phones || "",
            template_customer: val.template_customer || "",
            template_admin: val.template_admin || "",
            is_active_bool: val.is_active === 1,
          };
          this.signatureChanged = false;
          this.webhookUrl = this.buildWebhookUrl(val.webhook_token);
        } else {
          this.form = {
            name: "",
            device_id: null,
            webhook_signature: "",
            event_filter: "",
            admin_phones: "",
            template_customer: DEFAULT_CUSTOMER_TEMPLATE,
            template_admin: DEFAULT_ADMIN_TEMPLATE,
            is_active_bool: true,
          };
          this.signatureChanged = false;
          this.webhookUrl = "";
        }
      },
    },
  },
  methods: {
    buildWebhookUrl(token) {
      if (!token) return "";
      const base = process.env.VUE_APP_BASE_URL || "https://api.starsender.online/api/v1";
      return base.replace(/\/+$/, "") + "/webhook/mayar/" + token;
    },
    async copyUrl() {
      try {
        await navigator.clipboard.writeText(this.webhookUrl);
        this.$toast.success("Webhook URL di-copy");
      } catch (err) {
        this.$toast.error("Gagal copy");
      }
    },
    async submit() {
      this.isSaving = true;
      try {
        const payload = {
          name: this.form.name,
          device_id: parseInt(this.form.device_id),
          event_filter: this.form.event_filter || null,
          admin_phones: this.form.admin_phones,
          template_customer: this.form.template_customer,
          template_admin: this.form.template_admin,
          is_active: this.form.is_active_bool ? 1 : 0,
        };

        if (!this.isEdit || this.signatureChanged) {
          payload.webhook_signature = this.form.webhook_signature;
        }

        if (this.isEdit) {
          payload.id = this.editItem.id;
          const res = await this.$store.dispatch("mayar_update", payload);
          this.$toast.success(res.data.message || "Tersimpan");
        } else {
          const res = await this.$store.dispatch("mayar_create", payload);
          this.$toast.success(res.data.message || "Integrasi dibuat");
          const created = res.data && res.data.data && res.data.data.integration;
          if (created) {
            this.webhookUrl = this.buildWebhookUrl(created.webhook_token);
          }
        }
        this.$emit("saved");
      } catch (err) {
        this.$toast.error(err.response?.data?.message || "Gagal menyimpan");
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
code {
  background: #f1f1f1;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 12px;
  color: #d63384;
}
</style>
